import { getGenericBounds } from '../../lib/bound';
import { pointInShape, pointOnShape } from '../../lib/collision';
import {
  addElement,
  defaultGenericElement,
  type GenericElement,
} from '../../lib/element';
import { prepareElementForExport } from '../../lib/exportImport';
import { type Plugin } from '../../lib/plugin';
import { general } from '../general';

import { getRetouchingShape } from './utils/shape';

export interface RetouchingElement extends GenericElement {
  strokeColor: string;
  type: 'retouching';
  svgPath: string;
}

const retouchingState = {
  newStencilColor: '#3c87f7',
  newStencilSize: 50,
};

const retouchingActions = {
  activate: () => {
    general.actions.activate(plugin.name, true);
  },
  setStencilColor: (color: string) => {
    retouchingState.newStencilColor = color;
  },
  setStencilSize: (size: number) => {
    retouchingState.newStencilSize = size;
  },
};

const plugin: Plugin<
  RetouchingElement,
  typeof retouchingActions,
  typeof retouchingState
> = {
  name: 'retouching',
  actions: retouchingActions,
  state: retouchingState,
  cursor: 'default',
  events: {
    onPointerDown: ({ elementState, event, helpers, runtimeState }) => {
      const { button, pos } = event;

      if (button === 'left' && runtimeState.activeTool === 'retouching') {
        const x = pos.x - retouchingState.newStencilSize / 2;
        const y = pos.y - retouchingState.newStencilSize / 2;

        const newElement: RetouchingElement = {
          ...defaultGenericElement(),
          height: retouchingState.newStencilSize,
          originX: x,
          originY: y,
          svgPath:
            'm12.01 16.767-3.736 3.736c-.334.331-.74.497-1.218.497-.479 0-.884-.166-1.216-.497L3.497 18.16A1.663 1.663 0 0 1 3 16.94c0-.478.166-.883.497-1.215l3.745-3.745-3.717-3.717a1.688 1.688 0 0 1-.507-1.216c-.006-.48.157-.885.489-1.217L5.84 3.497C6.174 3.166 6.58 3 7.06 3c.478 0 .883.166 1.215.497L12 7.224l3.726-3.727c.334-.331.74-.497 1.218-.497.479 0 .884.166 1.216.497l2.343 2.343c.331.334.497.74.497 1.219 0 .478-.166.883-.497 1.215l-3.736 3.735 3.726 3.726c.338.336.507.744.507 1.224 0 .481-.166.888-.497 1.22l-2.306 2.305a1.691 1.691 0 0 1-1.231.511 1.66 1.66 0 0 1-1.23-.502l-3.727-3.726Zm0-5.636a.837.837 0 0 0 .62-.25.846.846 0 0 0 .248-.62.837.837 0 0 0-.25-.62.845.845 0 0 0-.62-.248.838.838 0 0 0-.62.25.846.846 0 0 0-.248.62c0 .247.084.453.25.62a.845.845 0 0 0 .62.248Zm-3.764-.154 2.74-2.74L7.26 4.51a.277.277 0 0 0-.203-.084.277.277 0 0 0-.203.084L4.52 6.844a.277.277 0 0 0-.083.204c0 .08.027.148.083.203l3.726 3.726Zm2.027 1.892a.838.838 0 0 0 .618-.25.845.845 0 0 0 .25-.62.837.837 0 0 0-.251-.62.845.845 0 0 0-.62-.248.837.837 0 0 0-.62.25.845.845 0 0 0-.248.62c0 .247.083.454.25.62a.846.846 0 0 0 .62.248Zm1.738 1.738a.838.838 0 0 0 .619-.25.846.846 0 0 0 .248-.62.837.837 0 0 0-.25-.62.845.845 0 0 0-.62-.248.837.837 0 0 0-.62.25.846.846 0 0 0-.248.62c0 .248.084.454.25.62a.845.845 0 0 0 .62.248Zm1.738-1.738a.837.837 0 0 0 .619-.25.845.845 0 0 0 .249-.62.838.838 0 0 0-.25-.62.846.846 0 0 0-.621-.248.838.838 0 0 0-.619.25.845.845 0 0 0-.249.62c0 .247.084.454.25.62a.845.845 0 0 0 .621.248Zm-.726 2.885 3.735 3.735a.276.276 0 0 0 .203.084c.08 0 .148-.028.204-.084l2.334-2.333a.277.277 0 0 0 .083-.204.277.277 0 0 0-.083-.203l-3.736-3.736-2.74 2.74Z',
          strokeColor: retouchingState.newStencilColor,
          type: 'retouching',
          width: retouchingState.newStencilSize,
          x: x,
          y: y,
        };

        addElement(elementState.elements, {
          ...newElement,
          shape: { type: 'area', data: newElement.svgPath },
        });

        helpers.renderStaticScene();
      }
    },
  },
  render: {
    draw: ({ config, context, element }) => {
      if (element.isDeletedFlag) {
        context.globalAlpha = config.canvas.deletingElementOpacity;
      }

      const scaleX = element.width / 24;
      const scaleY = element.height / 24;

      const path = new Path2D(element.svgPath);

      const m = document
        .createElementNS('http://www.w3.org/2000/svg', 'svg')
        .createSVGMatrix();
      const p = new Path2D();
      const t = m.scale(scaleX, scaleY);
      p.addPath(path, t);

      context.fillStyle = element.strokeColor;
      context.fill(p);
    },
  },
  collision: {
    bounds: (element) => getGenericBounds(element),
    calculateShape: (element) => getRetouchingShape(element),
    isPointInShape: (point, element) =>
      pointInShape(point, element.shape.data, 4),
    isPointOnShape: (point, element) =>
      pointOnShape(point, element.shape.data, 4),
  },
  transformation: {
    shiftBySizeOnFlip: true,
    alwaysKeepAspectRatio: true,
    resizedBounds: (element, nextWidth, nextHeight) => [
      element.x,
      element.y,
      element.x + nextWidth,
      element.y + nextHeight,
      0,
      0,
    ],
  },
  export: (element) => {
    return prepareElementForExport(element) as RetouchingElement;
  },
  import: (element) => {
    const imported = {
      ...defaultGenericElement(),
      ...element,
    } as RetouchingElement;

    imported.shape = getRetouchingShape(imported);

    return imported;
  },
};

export const retouching = plugin;
